/**
 * Configuração centralizada de cache para React Query
 * 
 * Matriz de Cache baseada em volatilidade dos dados:
 * - STATIC: Dados que raramente mudam (subjects, static content)
 * - PUBLIC: Dados públicos que mudam pouco (teacher profiles, classes)
 * - PROFILE: Dados de perfil do usuário (estáveis durante sessão)
 * - DYNAMIC: Dados que mudam frequentemente (bookings, availability)
 * - REALTIME: Dados que devem estar sempre frescos (notifications)
 */

export const CACHE_TIMES = {
  /**
   * Dados estáticos - Raramente mudam
   * Ex: subjects, áreas de conhecimento, configurações de plataforma
   */
  STATIC: {
    staleTime: 24 * 60 * 60 * 1000, // 24 horas
    gcTime: 48 * 60 * 60 * 1000,    // 48 horas
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
  
  /**
   * Dados públicos - Mudam pouco
   * Ex: teacher profiles, public classes, teacher directory
   * 🚀 OTIMIZADO: Removido refetchOnMount para evitar queries desnecessárias
   */
  PUBLIC: {
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 15 * 60 * 1000,   // 15 minutos
    refetchOnWindowFocus: true,
    refetchOnMount: false, // 🚀 Removido 'always' - usa cache
    refetchOnReconnect: true,
  },
  
  /**
   * Dados de perfil - Estáveis durante sessão
   * Ex: user profile, roles, preferences
   */
  PROFILE: {
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 20 * 60 * 1000,    // 20 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  },
  
  /**
   * Dados dinâmicos - Mudam frequentemente
   * Ex: bookings, availability slots, student list
   */
  DYNAMIC: {
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000,    // 5 minutos
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: true,
  },
  
  /**
   * Dados realtime - Sempre frescos
   * Ex: notifications, live status, active sessions
   */
  REALTIME: {
    staleTime: 30 * 1000,     // 30 segundos
    gcTime: 60 * 1000,        // 1 minuto
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  },
  
  /**
   * Dados admin - Cache moderado
   * Ex: analytics, metrics, audit logs
   */
  ADMIN: {
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 15 * 60 * 1000,   // 15 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },
} as const;

/**
 * Utility para criar configurações customizadas
 */
export const createCacheConfig = (
  category: keyof typeof CACHE_TIMES,
  overrides?: Partial<typeof CACHE_TIMES[keyof typeof CACHE_TIMES]>
) => ({
  ...CACHE_TIMES[category],
  ...overrides,
});
